import type { MinimalGlossaryTerm } from "@/app/(marketing)/(payload)/dictionary/_actions/glossary";
import { GlossaryTermCategoryBadge } from "@/app/(marketing)/(payload)/dictionary/_components/glossary-term-category-badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import Link from "next/link";
import type { ReactNode } from "react";
import reactStringReplace from "react-string-replace";

export type GlossaryRenderer = (text: string) => ReactNode;

type KeywordItem = {
  keyword: string;
  regex: RegExp;
  term: MinimalGlossaryTerm;
};

const keywordCache = new WeakMap<MinimalGlossaryTerm[], KeywordItem[]>();

function escapeRegExp(input: string) {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function replaceKeywords(
  keywords: KeywordItem[],
  text: string,
  usedTerms: Set<string>,
): ReactNode[] {
  let result: ReactNode[] = [text];

  for (const { regex, term } of keywords) {
    const newResult: ReactNode[] = [];

    for (const segment of result) {
      if (usedTerms.has(term.slug)) {
        newResult.push(segment);
        continue;
      }

      if (typeof segment === "string") {
        const replaced = reactStringReplace(segment, regex, (match, i) => {
          if (usedTerms.has(term.slug)) return match;
          usedTerms.add(term.slug);

          return (
            <HoverCard key={`${term.slug}-hovercard-${i}`}>
              <HoverCardTrigger asChild>
                <Link
                  key={`${term.slug}-keyword-${i}`}
                  href={`/dictionary/${term.slug}`}
                  prefetch={false}
                  className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                >
                  {match}
                </Link>
              </HoverCardTrigger>

              <HoverCardPortal>
                <HoverCardContent className="w-80 max-w-[calc(100vw_-_2rem)]">
                  <div className="flex flex-col gap-2">
                    <div className="flex">
                      <GlossaryTermCategoryBadge category={term.category} />
                    </div>

                    <div className="font-semibold">{term.term}</div>
                    <div className="text-sm">{term.seo.shortDefinition}</div>
                    {term.slug === "letter-of-intent-loi" && (
                      <p className="text-sm">
                        Some clauses can be binding. Have your lawyer review it
                        before you sign.
                      </p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCardPortal>
            </HoverCard>
          );
        });

        newResult.push(...replaced);
      } else {
        newResult.push(segment);
      }
    }

    result = newResult;
  }

  return result;
}

function buildGlossaryKeywords(terms: MinimalGlossaryTerm[]): KeywordItem[] {
  const keywords: KeywordItem[] = [];
  const trackedKeywords = new Set<string>();

  // add the actual terms first
  for (const term of terms) {
    if (trackedKeywords.has(term.term)) {
      continue;
    }

    keywords.push({
      keyword: term.term,
      regex: new RegExp(`\\b(${escapeRegExp(term.term)})\\b`, "i"),
      term,
    });

    trackedKeywords.add(term.term);
  }

  // then add the target keywords
  for (const term of terms) {
    for (const { keyword } of term.seo.triggerKeywords || []) {
      if (trackedKeywords.has(keyword)) {
        continue;
      }

      keywords.push({
        keyword,
        regex: new RegExp(`\\b(${escapeRegExp(keyword)})\\b`, "i"),
        term,
      });

      trackedKeywords.add(keyword);
    }
  }

  const sortedKeywords = keywords.sort(
    (a, b) => b.keyword.length - a.keyword.length,
  );

  return sortedKeywords;
}

/** Reuses the blog pattern: link the first mention of each term, with a short definition. */
export function createGlossaryTextRenderer(
  terms: MinimalGlossaryTerm[],
): GlossaryRenderer {
  let keywords = keywordCache.get(terms);
  if (!keywords) {
    keywords = buildGlossaryKeywords(terms);
    keywordCache.set(terms, keywords);
  }
  const usedTerms = new Set<string>();
  return (text) => replaceKeywords(keywords, text, usedTerms);
}

export function GlossaryText({
  text,
  terms = [],
}: {
  text: string;
  terms?: MinimalGlossaryTerm[];
}) {
  return <>{createGlossaryTextRenderer(terms)(text)}</>;
}
